import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import {
  getApiUserContext,
  useGetApiUserContext,
} from '@unifyapps/network/generated/user-context-rest-api/user-context-rest-api';
import { useMemo } from 'react';
import { debugSSR } from '../../consts/debug';
import { EMPTY_OBJECT } from '../../consts/empty';
import type { UserRuntimeEnvDetails } from '../nocode/types';

function getUserContextDetails({
  userDetails,
  userRuntimeEnvDetails,
}: {
  userDetails: CurrentUserDetails;
  userRuntimeEnvDetails?: UserRuntimeEnvDetails;
}) {
  return {
    userDetails: {
      ...userDetails,
      user: {
        ...(userDetails.user || EMPTY_OBJECT),
      },
    } as CurrentUserDetails,
    // typecasting since roles doesnt exist on CurrentUserDetails type
    userRuntimeEnvDetails,
  };
}

export async function fetchUserDetails(userRuntimeEnvDetails?: UserRuntimeEnvDetails) {
  const userDetails = await getApiUserContext({ includeRoles: true });
  if (!userDetails) {
    return null;
  }
  return getUserContextDetails({ userDetails, userRuntimeEnvDetails });
}

export function useGetCurrentUserDetails({
  isLoggedIn,
  userRuntimeEnvDetails,
}: {
  isLoggedIn: boolean;
  userRuntimeEnvDetails?: UserRuntimeEnvDetails;
}) {
  const {
    data,
    isPending,
    isLoading: isLoadingUserContext,
  } = useGetApiUserContext(
    {
      includeRoles: true,
    },
    {
      query: {
        enabled: isLoggedIn,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
      },
    },
  );

  const currentUserDetails = useMemo(() => {
    if (!data) return undefined;
    return getUserContextDetails({
      userDetails: data,
      userRuntimeEnvDetails,
    });
  }, [data, userRuntimeEnvDetails]);

  debugSSR(
    'useGetCurrentUserDetails',
    {
      isLoading: isLoggedIn ? isPending || isLoadingUserContext : false,
      isPending,
      isLoadingUserContext,
    },
    undefined,
  );

  return {
    currentUserDetails,
    //if user is not loggedin then user will not be present, hence loading is false
    //as isPending  is always true id query is disabled
    isLoading: isLoggedIn ? isPending || isLoadingUserContext : false,
  };
}
